import { MeRepository } from 'common/repositories/index';
import { Session, SessionKeys } from 'common/services/-RFC.session.js';
import i18nPromise from 'initi18n';
import Cookie from 'common/services/util/-RFC.Cookie';
import Mixpanel from 'common/services/stats/-RFC.Mixpanel';

const _serviceDependencies = ['$q', '$rootScope', '$location', '$window', 'Util', 'Auth',
    'Redirect', 'Sso', '$http', '$state'];

/**
* @ngdoc service
* @name services.RootResolver
*
* @description
* This class is the Application Root Resolver. Here we check:
* 1. if the user is anonymous
* 2. if the user's email is confirmed
* 2. if token exists
* 3. if the URL has parameters
* After this steps we redirect the user accordingly.
*/
class RootResolver {

/**
 * @param  {any} $q
 * @param  {any} $rootScope
 * @param  {any} $location
 * @param  {any} $window
 * @param  {any} Util
 * @param  {any} Auth
 * @param  {any} Redirect
 * @param  {any} Sso
 */

    constructor(
        $q, $rootScope, $location, $window, Util, Auth,
        Redirect, Sso, $http, $state,
    ) {


        $rootScope.State.loading = true;

        this.rootPromise = $q.defer();
        this.$rootScope = $rootScope;
        this.$window = $window;
        this.Util = Util;
        this.Auth = Auth;
        this.Redirect = Redirect;
        this.path = $location.path();
        this.MeRepository = MeRepository;
        this.meSubscriber = {};
        this.Sso = Sso;
        this.hasSeenConfirmationScreen = false;
        this.$state = $state;

        this.initialUrl = String(Session.getValue(SessionKeys.InitialUrl));

        $http({

            method: 'GET',
            url: `${CWCONFIG.restApi.url}ping`,
        }).then((response) => {

            if (response.headers) {

                const headers = response.headers();
                $rootScope.requestCountry = Util.deepFind(headers, 'x-avira-country-code');

            }

        }).catch((error) => {

            const errorObj = this.Util.deepFind(error, 'data.errors')[0];
            const errorCode = this.Util.deepFind(errorObj, 'code');

            if (errorCode === '929') {

                $rootScope.isComingFromEmbargoedCountry = true;

                Mixpanel.send('ConnectWeb Restricted Country');
                if (this.initialUrl.indexOf('client_id=spotlight') > -1) {

                    this.$state.go('complianceFailed');

                }

            }

        });

        // SSO save extra url params for thirdParty login
        this.Sso.saveExtraParams();

        // observable for MeRepository
        const meObservable = MeRepository.getObservable();
        const onNextMe = (user) => {

            // if user exists and is not newly registered
            // we need to show the confirmation page later because the launcher authentication
            // depends on the second /oauth POST we make after we create the user
            if (user && !this.$rootScope.newRegisteredUser) {

                this.checkUserAndRedirect();

            }

        };
        this.meSubscriber = meObservable.subscribe(onNextMe, () => { }, () => { });

        // whether the translations used for the react components
        // load we still want to show the dashboard currently
        // this behaviour might change in the future
        // also wait until the SSO exhanges the received JWT with a dashboard JWT
        Promise.all([i18nPromise(), this.Sso.exchangeToken()])
            .then((params) => {

                this.init(params);

            })
            .catch(() => {

                this.init();

            });

        return this.rootPromise.promise;

    }

    init(params) {

        this.userHasToken = this.Util.getToken();
        const functionParams = params || [];
        const tokenScope = functionParams.find(item => (item === 'passwords'));

        /* if token exists and the URL has no extra params, get user data from backend */
        if (this.userHasToken &&
            !this.urlHasExtraParams() &&
            this.Sso.enableAutoLogon() &&
            tokenScope !== 'passwords') {

            this.Auth.getMeAsync();

        } else {

            /* if the url has extra params like jwt,
            myat etc, we resolve the promise and we don't get user data from here.
            The GET command with the user data will be executed from Auth.js service.  */
            this.rootPromise.resolve();

            /* hide loading state after root resolver is ready and url has no params */
            if (!this.urlHasExtraParams()) {

                this.$rootScope.State.loading = false;

            }

        }

    }

    // check if the url has extra params
    urlHasExtraParams() {

        return !!(this.path.indexOf('/auth/login') === 0 && (
            /auth\/login\/t/.test(this.path) ||
            /auth\/login\/adt/.test(this.path) ||
            /auth\/login\/myat/.test(this.path) ||
            /auth\/login\/jwt/.test(this.path)));

    }

    // check user status and redirect him accordingly
    checkUserAndRedirect() {

        this.rootPromise.resolve();
        const user = this.MeRepository.getMyUser();
        const isNewUser = this.Util.deepFind(user, 'attributes.password_not_set');
        const gdprConfirmed = this.Util.deepFind(user, 'attributes.gdpr_confirm');
        const tfaStatus = this.Util.deepFind(user, 'attributes.status_tfa');
        const tfaCookie = this.Auth.getTfaCookie();
        const tfaSessionStorage = sessionStorage.getItem('TfaSessionStorage');
        const usCompliance = user.attributes.status_us_compliance_approved;
        const usComplianceRequested = user.attributes.status_us_compliance_requested;
        const loginType = Cookie.get(CWCONFIG.cookie.loginType);

        if (this.$rootScope.isComingFromEmbargoedCountry &&
            this.initialUrl.indexOf('client_id=spotlight') > -1) {

            this.$state.go('complianceFailed');

        } else if (this.Util.isAnonymous()) {

            if ((this.Sso.isThirdPartyLogin() && this.Sso.getEmailAddress()) ||
                this.initialUrl.indexOf('action=gotologin') > -1
            ) {

                this.Auth.gotoLogin();

            } else {

                // if the user is anonymous, go to register page
                this.Auth.gotoRegister();

            }

        } else if (usCompliance === null && !usComplianceRequested) {

            if (loginType === 'fb_auth' || loginType === 'gg_auth' || loginType === 'apl_auth') {

                this.$rootScope.socialLoginValidation = true;
                this.$rootScope.userCheckSync = true;
                this.Auth.gotoRegister();

            } else if (this.initialUrl.indexOf('client_id=spotlight') > -1 &&
                    this.initialUrl.indexOf('action=user_check_sync') > -1) {

                this.$rootScope.socialLoginValidation = true;
                this.$rootScope.userCheckSync = true;
                this.Auth.gotoRegister();

            } else if (isNewUser) {

                this.Auth.gotoSetPassword();

            } else {

                this.$rootScope.socialLoginValidation = true;
                this.Auth.gotoRegister();

            }

        } else if (!gdprConfirmed || gdprConfirmed === '') {

            this.hasSeenConfirmationScreen = true;
            this.Auth.gotoConfirm();

        } else if (tfaStatus === 'active' && !tfaCookie && !tfaSessionStorage && this.path === '/auth/login') {

            /* if user's two-factor authentification is active and the browser is not trusted,
            it means that the user should go through all login steps (must be logged out) */
            this.Auth.logOut();

        } else if (this.Sso.isThirdPartyLogin()) {

            // SSO check for thirdPartyLogin and
            // redirect user after login to the received redirect_uri with the token
            this.Sso.redirect();


        } else if (isNewUser) {

            // if user doesn't have a password
            this.Auth.gotoSetPassword();

        } else if (!user.attributes.email) {

            /* if user's email or option attributes are null and is not anonymous, it means that
            the user configuration has a problem and must be logged out */
            this.Auth.logOut();

        } else {

            /* this condition prevents overwriting the dashboard language from URL parameter.
            When you are logged in dasbhoard,
            the language can be changed only from My Account options */
            if (CWCONFIG.language !== user.attributes.language) {

                // Keep the received params in auth page
                const extraUrlParams = Session.getValue(SessionKeys.ExtraUrlParams);

                const acceptedDomains = [
                    'https://my.avira.com',
                    'https://oeacc.avira.com',
                    'https://beta.my.avira.com',
                    'https://beta.oeacc.avira.com',
                ];

                let isAcceptedDomain = false;

                acceptedDomains.forEach((domain) => {

                    if (window.location.href.indexOf(domain) === 0) {

                        isAcceptedDomain = true;

                    }

                });

                const unsafeCharacters = ['(', ')', '[', ']', '{', '}', '<', '>', '^', '|', 'javascript'];

                const hasUnsafeChars = unsafeCharacters.find(char =>
                    this.$window.location.href.indexOf(char) > -1);

                if (isAcceptedDomain && !hasUnsafeChars) {

                    if (extraUrlParams) {

                        // this condition prevents the duplication of the parameters
                        if (this.$window.location.href.indexOf(extraUrlParams) === -1) {

                            this.$window.location.href = `${this.$window.location.href.replace(
                                CWCONFIG.language,
                                user.attributes.language,
                            )}${extraUrlParams}`;

                        } else {

                            this.$window.location.href = this.$window.location.href.replace(
                                CWCONFIG.language,
                                user.attributes.language,
                            );

                        }

                    } else {

                        this.$window.location.pathname = this.$window.location.pathname.replace(
                            CWCONFIG.language,
                            user.attributes.language,
                        );

                    }

                } else {

                    this.$window.location.href = 'https://my.avira.com';

                }

            }

            // close confirmation screen from Spotlight if the user is confirmed
            if (this.hasSeenConfirmationScreen &&
                window.external &&
                ('CloseHtml' in window.external)) {

                window.external.CloseHtml('confirmed');

            } else {

                /* redirect to dashboard if the user's email is confirmed
                and the route is not already /dashboard */

                // Some URLs need to be redirected (old routes)
                this.Redirect.redirectUrl();

            }

            /* unsubscribe meObservable after we make sure the user
            is not anonymous or the email is confirmed */
            this.meSubscriber.dispose();

        }

    }

}

_serviceDependencies.push(RootResolver);

angular.module('services')
    .service('RootResolver', _serviceDependencies);



// WEBPACK FOOTER //
// ./scripts/common/resolvers/rootResolver.js